import { remove } from "lodash-es";
import { type FC, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { TriggerButton, TriggerButtonIntent, TriggerButtonSize } from "widgets";

import { type InputTableQuerySpec } from "@/graphql.ts";
import { getTableDataSlice, setExpansionPath } from "@/redux/modules/table-data.ts";
import { type IAppState } from "@/redux/reducer.ts";
import { type ReduxDispatch } from "@/redux/redux-utils.ts";

interface HeaderGroupExpansionIconCellProps {
  tableQuerySpec: InputTableQuerySpec;
  expandableField: string;
}

export const ExpansionIconCell: FC<HeaderGroupExpansionIconCellProps> = (props) => {
  const { tableQuerySpec, expandableField } = props;

  const dispatch = useDispatch<ReduxDispatch>();

  const expansionPath = useSelector<IAppState, string[]>(
    (state) => getTableDataSlice(state.tableData, tableQuerySpec)?.expansionPath,
  );

  const isColExpanded = useMemo<boolean>(() => {
    return !!expansionPath.find((expansionKey) => expansionKey === expandableField);
  }, [expandableField, expansionPath]);

  const handleToggleExpand = useCallback(() => {
    const updatedExpansionPath = [...expansionPath];
    if (isColExpanded) {
      remove(updatedExpansionPath, (key) => key === expandableField);
    } else {
      updatedExpansionPath.push(expandableField);
    }

    dispatch(setExpansionPath(tableQuerySpec, updatedExpansionPath));
  }, [dispatch, expansionPath, expandableField, isColExpanded, tableQuerySpec]);

  return (
    <TriggerButton
      icon={isColExpanded ? "unfold_less_double" : "unfold_more_double"}
      label={isColExpanded ? "Collapse" : "Expand"}
      size={TriggerButtonSize.Mini}
      onPress={handleToggleExpand}
      intent={isColExpanded ? TriggerButtonIntent.Active : TriggerButtonIntent.Secondary}
    />
  );
};
